import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, afterNextRender, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { BetslipMobileWidgetComponent } from '../betslip/betslip-mobile-widget.component';
import { BetSlipBonusHint, BetSlipSummaryViewModel } from '../betslip/betslip-ticket.models';
import { MatchSearchMobilePanelComponent } from '../match-search/match-search-mobile-panel.component';

@Component({
  selector: 'app-mobile-shortcuts',
  imports: [RouterLink, RouterLinkActive, TranslateModule, DecimalPipe, BetslipMobileWidgetComponent, MatchSearchMobilePanelComponent],
  templateUrl: './mobile-shortcuts.component.html',
  styleUrl: './mobile-shortcuts.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[style.bottom.px]': 'keyboardInset()' }
})
export class MobileShortcutsComponent {
  readonly summary = input.required<BetSlipSummaryViewModel>();
  readonly maxWinPulseKey = input.required<number>();
  readonly searchEnabled = input.required<boolean>();
  readonly searchQuery = input.required<string>();
  readonly searchOpen = input(false);
  readonly liveActive = input(false);
  readonly bonusHint = input<BetSlipBonusHint | null>(null);
  readonly toggleSearch = output<void>();
  readonly closeSearch = output<void>();
  readonly openBetSlip = output<void>();
  readonly openLive = output<void>();
  protected readonly keyboardInset = signal(0);
  protected readonly bonusVisible = signal(false);
  private readonly searchButton = viewChild.required<ElementRef<HTMLButtonElement>>('searchButton');
  private readonly liveButton = viewChild.required<ElementRef<HTMLButtonElement>>('liveButton');
  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private previousSelectionCount = 0;
  private bonusTimer: ReturnType<typeof setTimeout> | undefined;
  private bonusHovered = false;
  private bonusFocused = false;

  constructor() {
    afterNextRender(() => {
      const element = this.hostElement.nativeElement;
      const root = element.ownerDocument.documentElement;
      const observer = new ResizeObserver(() => {
        root.style.setProperty('--sb-mobile-dock-height', `${element.getBoundingClientRect().height}px`);
      });
      observer.observe(element);
      this.destroyRef.onDestroy(() => {
        observer.disconnect();
        root.style.removeProperty('--sb-mobile-dock-height');
      });
    });
    this.destroyRef.onDestroy(() => clearTimeout(this.bonusTimer));

    effect(() => {
      const count = this.summary().selectionCount;
      const hint = this.bonusHint();
      const addedSelection = count > this.previousSelectionCount;
      const removedSelection = count < this.previousSelectionCount;
      this.previousSelectionCount = count;
      if (!hint || removedSelection) {
        this.dismissBonus();
      } else if (addedSelection) {
        this.bonusVisible.set(true);
        this.restartBonusTimer();
      }
    });

    effect(onCleanup => {
      const viewport = window.visualViewport;
      if (!this.searchOpen() || !viewport) {
        this.keyboardInset.set(0);
        return;
      }

      // Mobile keyboards shrink the visual viewport while fixed elements still
      // use the layout viewport. Keep the input and its dock above the keyboard.
      const updateInset = (): void => this.keyboardInset.set(
        viewport.scale === 1 ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop) : 0
      );
      updateInset();
      viewport.addEventListener('resize', updateInset);
      viewport.addEventListener('scroll', updateInset);
      onCleanup(() => {
        viewport.removeEventListener('resize', updateInset);
        viewport.removeEventListener('scroll', updateInset);
      });
    });
  }

  protected dismissSearch(): void {
    this.closeSearch.emit();
    this.searchButton().nativeElement.focus({ preventScroll: true });
  }

  focusLive(): void {
    this.liveButton().nativeElement.focus({ preventScroll: true });
  }

  protected dismissBonus(restoreFocus = false): void {
    const shouldRestoreFocus = restoreFocus || this.bonusFocused;
    clearTimeout(this.bonusTimer);
    this.bonusVisible.set(false);
    this.bonusHovered = false;
    this.bonusFocused = false;
    if (shouldRestoreFocus) {
      this.focusLive();
    }
  }

  protected setBonusHovered(hovered: boolean): void {
    this.bonusHovered = hovered;
    this.restartBonusTimer();
  }

  protected setBonusFocused(focused: boolean): void {
    this.bonusFocused = focused;
    this.restartBonusTimer();
  }

  private restartBonusTimer(): void {
    clearTimeout(this.bonusTimer);
    if (!this.bonusHovered && !this.bonusFocused) {
      this.bonusTimer = setTimeout(() => this.dismissBonus(), 8000);
    }
  }
}
